
import { useEffect, useState } from 'react';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import { DataGrid, GridRowsProp, GridColDef, GridCellParams, GridRowData } from '@mui/x-data-grid';
import { Grid, IconButton, Typography } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { KeyTypes, StringOperatorTypes } from '../app/enums';
import { ConfirmDeleteDialog } from '../components/Dialogs/ConfirmDeleteDialog';
import { SuccessDialog } from './Dialogs/SuccessDialog';
import { RulesType } from '../models/matcher.interface';
import { MatcherGroups } from '../api/api';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { EditRuleDialog } from './Dialogs/EditRuleDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    margin: {
      margin: theme.spacing(1),
    },
    dataGridRoot: {
      border: 0,
      '& .header': {
        background: theme.palette.background.default,
        color: theme.palette.primary.dark,
        fontWeight: '600',
        fontSize: 16,
        padding: theme.spacing(0),
      },
      '& .MuiDataGrid-menuIconButton': {
        color: theme.palette.primary.dark,
        size: 'small',
        padding: theme.spacing(0),
      },
      '& .MuiDataGrid-sortIcon': {
        color: theme.palette.primary.dark,
        size: 'small',
        padding: theme.spacing(0),
      },
      headerHeight: 45,
      rowHeight: 40,
      pageSize: 10,
    },
  }),
);

export interface IExistingRulesGrid {
  setError: (error: string) => void
  rules: RulesType[]
  loadRules: () => void
}

export const ExistingRulesGrid = (
  { setError, rules, loadRules }: IExistingRulesGrid) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [editId, setEditId] = useState('');
  const [make, setMake] = useState<string | undefined>()
  const [model, setModel] = useState<string | undefined>()
  const [minYear, setMinYear] = useState<string | undefined>()
  const [maxYear, setMaxYear] = useState<string | undefined>()
  const [keyType, setKeyType] = useState<string | undefined>()
  const [price, setPrice] = useState<string | undefined>()
  const [company, setCompany] = useState<string | undefined>()
  const [groupId, setGroupId] = useState<string | undefined>()
  const [deleteInfo, setDeleteInfo] = useState<any>();
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [page, setPage] = useState(0);

  useEffect(() => {
    loadRules();
  }, []);

  const columns: GridColDef[] = [
    {
      field: 'make',
      headerClassName: 'header',
      headerAlign: 'center',
      headerName: 'Make',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <div>
          {params.getValue(params.id, 'makeOperator') !== 'EQUALS' &&
            <Typography style={{ fontStyle: "italic" }} variant="caption" display="inline" color="primary">
              {StringOperatorTypes[params.getValue(params.id, 'makeOperator') as keyof typeof StringOperatorTypes]} &nbsp;
            </Typography>
          }
          <Typography variant="inherit" display="inline">
            {params.value}
          </Typography>
        </div>
      )
    },
    {
      field: 'model',
      headerClassName: 'header',
      headerAlign: 'center',
      headerName: 'Model',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <div>
          {params.getValue(params.id, 'modelOperator') !== 'EQUALS' &&
            <Typography style={{ fontStyle: "italic" }} variant="caption" display="inline" color="primary">
              {StringOperatorTypes[params.getValue(params.id, 'modelOperator') as keyof typeof StringOperatorTypes]} &nbsp;
            </Typography>
          }
          <Typography variant="inherit" display="inline">
            {params.value}
          </Typography>
        </div>
      )
    },
    {
      field: 'minYear',
      headerClassName: 'header',
      headerAlign: 'center',
      headerName: 'Min Year',
      flex: 1,
      valueFormatter: (params) => {
        return Number(params.value)
      }
    },
    {
      field: 'maxYear',
      headerClassName: 'header',
      headerAlign: 'center',
      headerName: 'Max Year',
      flex: 1,
      valueFormatter: (params) => {
        return Number(params.value)
      }
    },
    {
      field: 'keyType',
      headerClassName: 'header',
      headerAlign: 'center',
      headerName: 'Key Type',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Typography variant="inherit" display="inline">
          {params.value
            ? KeyTypes[params.value as keyof typeof KeyTypes]
            : KeyTypes.UNSUPPORTED
          }
        </Typography>
      )
    },
    {
      field: 'price',
      headerClassName: 'header',
      headerAlign: 'center',
      headerName: 'Price',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <NumberFormat
          value={params.value + ''}
          displayType={'text'}
          thousandSeparator={true}
          prefix={'$'}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      ),
    },
    {
      field: 'company',
      headerClassName: 'header',
      headerAlign: 'center',
      headerName: 'Company',
      flex: 1,
      renderCell: (params: GridCellParams) => (
        <Typography variant="inherit" display="inline">
          {params.value
            ? params.value
            : "Any"
          }
        </Typography>
      )
    },
    // {
    //   field: 'edit',
    //   headerClassName: 'header',
    //   headerAlign: 'center',
    //   align: 'center',
    //   headerName: 'Edit',
    //   sortable: false,
    //   renderCell: (params: GridCellParams) => (
    //     <Grid container
    //       justifyContent="center"
    //       alignItems="center"
    //       style={{ minHeight: '10vh' }}
    //     >
    //       <IconButton aria-label="edit"
    //         className={classes.margin}
    //         onClick={() => showEditDialog(params)}
    //         color="primary"
    //       >
    //         <EditOutlinedIcon fontSize="medium" />
    //       </IconButton>
    //     </Grid>
    //   ),
    //   disableColumnMenu: true,
    //   flex: 1
    // },
    {
      field: 'id',
      headerClassName: 'header',
      headerAlign: 'center',
      align: 'center',
      headerName: 'Delete',
      sortable: false,
      renderCell: (params: GridCellParams) => (
        <Grid container
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: '10vh' }}
        >
          <IconButton aria-label="delete"
            className={classes.margin}
            onClick={() => showDeleteDialog(params)}
            color="primary"
          >
            <DeleteOutlinedIcon fontSize="medium" />
          </IconButton>
        </Grid>
      ),
      disableColumnMenu: true,
      flex: 1
    },
  ];

  const rows: GridRowsProp = rules.map((item, index) => ({
    id: item.id,
    make: item.make,
    model: item.model,
    modelOperator: item.modelOperator,
    makeOperator: item.makeOperator,
    minYear: item.minYear,
    maxYear: item.maxYear,
    keyType: item.keyType,
    price: item.price,
    company: item.company,
  }));

  const handleDelete = (groupId: string) => {
    setOpen(false);

    MatcherGroups.deleteMatcherGroup(groupId)
      .then((returnedId) => {
        setDeleteSuccess(true);
        loadRules();
      })
      .catch((e) => {
        setDeleteSuccess(false);
        setError(e.response.data.message);
      });
  };

  const showDeleteDialog = (params: GridCellParams) => {
    setOpen(true);
    setDeleteInfo(getDeleteInfo(params.row))
    setDeleteId(params.id + '' || '');
  };
  
  const showEditDialog = (params: GridRowData) => {
    setEditId(params.id)
    let row = params.row
    setMake(row.make)
    setModel(row.model)
    setMinYear(row.minYear)
    setMaxYear(row.maxYear)
    setKeyType(row.keyType)
    setCompany(row.company)
    setPrice(row.price)
    setGroupId(row.id)
    setOpenEdit(true);
  };

  const handleCloseEdit = () => {
    setOpenEdit(false);
    loadRules()
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteClose = () => {
    setDeleteSuccess(false);
  };

  const getDeleteInfo = (row: GridRowData) => {
    let price: number = +row.price
    let min: number = +row.minYear
    let max: number = +row.maxYear
    let make = row.make.length > 0
      ? <p>Make: {row.make} </p>
      : ''
    let model = row.model.length > 0
      ? <p>Model: {row.model} </p>
      : ''
    let company = row.company.length > 0
      ? <p>Company: {row.company} </p>
      : ''
    let keyType = row.keyType
      ? KeyTypes[row.keyType as keyof typeof KeyTypes]
      : KeyTypes.UNSUPPORTED

    let pricePrint = price > 0
      ? <p>Price:
        <NumberFormat
          value={price + ''}
          displayType={'text'}
          thousandSeparator={true}
          prefix={'$'}
          decimalScale={2}
          fixedDecimalScale={true}
        />
      </p>
      : ''

    let minYear = min > 0
      ? <p>Min Year: {min}</p>
      : ''

    let maxYear = max > 0
      ? <p>Max Year: {max}</p>
      : ''

    let print = <div>{make} {model} {minYear} {maxYear}{company} Key Type: {keyType} {pricePrint}</div>

    return print
  }

  return (
    <>
      <div style={{ height: 450, width: '100%' }} className={classes.dataGridRoot}>
        <DataGrid
          rows={rows}
          columns={columns}
          pageSize={10}
          rowHeight={35}
          headerHeight={45}
          page={page}
          rowsPerPageOptions={[10]}
          onPageChange={(newPage) => setPage(newPage)}
        />
      </div>
      <EditRuleDialog
        open={openEdit}
        onClose={handleCloseEdit}
        make={make ?? ''}
        model={model ?? ''}
        minYear={minYear ?? ''}
        maxYear={maxYear ?? ''}
        keyType={keyType ?? ''}
        price={price ?? ''}
        company={company ?? ''}
        id={groupId ?? ''}
      />
      <ConfirmDeleteDialog
        title='Confirm Rule Delete'
        open={open}
        onClose={handleClose}
        id={deleteId}
        info={deleteInfo}
        onDelete={() => { handleDelete(deleteId) }}
      />
      <SuccessDialog
        open={deleteSuccess}
        onClose={handleDeleteClose}
        title='Delete Rule'
        content='Rule Delete Successful'
      />
    </>
  );
};

export default ExistingRulesGrid;
